'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import {
  IconCalendarStats,
  IconClock,
  IconCoins,
  IconListDetails,
  IconRulerMeasure,
  IconSpeakerphone,
} from '@tabler/icons-react';
import type { SeoStatisticsBlockResponseDto } from '@gen/types/SeoStatisticsBlockResponseDto';
import { formatStatisticValue } from './formatStatisticValue';
import { getStatisticKind } from './getStatisticKind';
import { cn } from '@/lib/utils';

type Props = {
  blocks?: SeoStatisticsBlockResponseDto[];
  updatedAt?: string | null;
};

const statisticIcons = {
  active: IconSpeakerphone,
  period: IconCalendarStats,
  price: IconCoins,
  area: IconRulerMeasure,
  default: IconListDetails,
};

const statisticTones = {
  active: '[--metric-accent:color-mix(in_srgb,var(--color-primary-green-day)_72%,#181818)]',
  period: '[--metric-accent:#2563d4]',
  price: '[--metric-accent:#a96800]',
  area: '[--metric-accent:#793cce]',
  default: '[--metric-accent:color-mix(in_srgb,var(--color-primary-green-day)_72%,#181818)]',
};

const unitClassName =
  'ms-2 inline-block text-[18px] font-medium tracking-normal text-[#535a57] @max-[480px]:ms-1 @max-[480px]:text-[16px]';

function StatisticIcon({ block }: { block: SeoStatisticsBlockResponseDto }) {
  const tone = getStatisticKind(block);
  const Icon = statisticIcons[tone];

  return (
    <span
      aria-hidden
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full text-(--metric-accent) bg-[color-mix(in_srgb,var(--metric-accent)_14%,var(--color-bg-day))]',
        statisticTones[tone],
      )}
    >
      {block.iconUrl ? (
        <Image
          unoptimized
          width={22}
          height={22}
          src={block.iconUrl}
          alt=''
          loading='lazy'
          className='size-5 object-contain'
        />
      ) : (
        <Icon className='size-5 object-contain' stroke={1.75} />
      )}
    </span>
  );
}

export function SeoStatistics({ blocks, updatedAt }: Props) {
  const t = useTranslations('seoStatistics');
  const locale = useLocale();
  const date = updatedAt ? new Date(updatedAt) : null;
  const validDate = date && !Number.isNaN(date.getTime()) ? date : null;

  if (!blocks?.length) return null;

  return (
    <section aria-label={t('title')} className='@container text-primary'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 @max-[480px]:flex-col @max-[480px]:items-start @max-[480px]:gap-1'>
        <h2 className='m-0 text-[20px] leading-[1.4] font-semibold tracking-[-0.02em] text-balance @max-[480px]:text-[18px]'>
          {t('title')}
        </h2>
        <div className='flex items-center gap-1.5 text-xs leading-[1.5] text-[#535a57]'>
          <IconClock aria-hidden className='size-3.5 shrink-0' stroke={1.75} />
          {validDate ? (
            <span>
              {t('updated')}{' '}
              <time dateTime={validDate.toISOString()}>
                {new Intl.DateTimeFormat(locale, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                }).format(validDate)}
              </time>
            </span>
          ) : (
            <span>{t('unknownDate')}</span>
          )}
        </div>
      </div>
      <dl
        className={cn(
          'm-0 grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-3 @max-[760px]:grid-cols-2 @max-[480px]:gap-2 @max-[360px]:grid-cols-1',
          blocks.length === 3 && '@min-[760px]:grid-cols-3',
          blocks.length === 4 && '@min-[760px]:grid-cols-4',
        )}
      >
        {blocks.map((block, index) => (
          <div
            key={`${block.title}-${index}`}
            className={cn(
              'flex min-w-0 flex-col items-start gap-6 rounded-2xl border border-primary-stroke-day bg-bg-day p-6 @max-[760px]:p-5 @max-[480px]:gap-4 @max-[480px]:p-4',
              (blocks.length === 1 || (blocks.length === 3 && index === 0)) && '@max-[760px]:col-span-full',
            )}
          >
            <dt className='flex min-h-[42px] w-full items-center gap-2.5 text-sm leading-[1.5] font-medium text-[#535a57] wrap-anywhere @max-[480px]:gap-2 @max-[480px]:text-[13px]'>
              <StatisticIcon block={block} />
              <span className='max-w-[28ch]'>{block.title}</span>
            </dt>
            <dd className='m-0 mt-auto max-w-full text-[length:clamp(32px,3.6cqi,44px)] leading-[1.15] font-semibold tracking-[-0.03em] tabular-nums wrap-anywhere'>
              <bdi>
                {formatStatisticValue(block.value, locale)}
                {block.currency && (
                  <span className={unitClassName}>{block.currency.sign || block.currency.currency}</span>
                )}
                {block.suffix && <span className={unitClassName}>{block.suffix}</span>}
              </bdi>
            </dd>
            {block.subtitle && (
              <dd className='m-0 -mt-4 text-xs leading-[1.6] text-[#535a57] wrap-anywhere @max-[480px]:-mt-2'>
                {block.subtitle}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </section>
  );
}
